import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import { noop } from "nodash"
import { Cursor, Viewer } from "@nova/constants"
import { GlobalNotificationContext } from "@nova/component-global-notification"

export type SliceState = {
    subscribers: Viewer[]
    viewers: Viewer[]
    mouseLocations: Record<string, Cursor["mouse"]>
    watching?: Viewer
    preparingToWatch?: Viewer
    triggerNotification?: boolean
}
const name = "pubsub-v1"
const initialState = {
    subscribers: [],
    viewers: [],
    mouse: { x: 0, y: 0 },
    mouseLocations: {},
    triggerNotification: false,
    userIdBySocketId: {},
}

const { actions, reducer } = createSlice({
    name,
    initialState: { ...initialState } as SliceState,
    reducers: {
        addViewer: (state, { payload }: PayloadAction<Viewer>) => {
            if (!state.viewers.find((e) => e.id === payload.id)) state.viewers.push(payload)
        },
        removeViewer: (state, { payload }: PayloadAction<string>) => {
            state.viewers = state.viewers.filter((viewer) => viewer.id !== payload)
            delete state.mouseLocations[payload]
        },
        setUpdatedViewers: (state, { payload }: PayloadAction<Viewer[]>) => {
            state.viewers = payload
        },
        addSubscriber: (state, { payload }: PayloadAction<Viewer>) => {
            state.subscribers.push(payload)
        },
        removeSubscriber: (state, { payload }: PayloadAction<string>) => {
            state.subscribers = state.subscribers.filter((viewer) => viewer.id !== payload)
        },
        prepareToWatch: (state, action: PayloadAction<Viewer>) => {
            state.preparingToWatch = action.payload
        },
        clearPreparingToWatch: (state) => {
            state.preparingToWatch = undefined
        },
        subscribeToMap: (state, action: PayloadAction<Viewer>) => {
            state.watching = action.payload
        },
        unsubscribeToMap: (state) => {
            state.watching = undefined
            state.triggerNotification = false
        },
        copyState: (state) => {
            state.watching = undefined
            state.triggerNotification = false
        },
        setWatching: (state, action: PayloadAction<Viewer | undefined>) => {
            state.watching = action.payload
        },
        pubsubTriggerNotification: (
            state: Draft<SliceState>,
            action: PayloadAction<{
                viewer?: Viewer
                customContext?: GlobalNotificationContext
            }>
        ) => {
            state.triggerNotification = !!(
                action.payload?.viewer?.id || action.payload?.customContext
            )
        },
        publishState: noop as VoidFunction,
        receiveState: noop as VoidFunction,
        disconnectSocket: noop as VoidFunction,
        clearPubsubState: () => {
            return { ...initialState }
        },
    },
})
const r = { [name]: reducer } as Record<typeof name, typeof reducer>
const selectors = withRoot((r: Record<typeof name, SliceState>) => r[name], {
    selectViewers: () => (s) => s.viewers,
    selectWatching: () => (s) => s.watching,
    selectPreparingToWatch: () => (s) => s.preparingToWatch,
    selectMouseLocations: () => (s) => s.mouseLocations,
    selectTriggerNotification: () => (s) => s.triggerNotification,
    selectSubscribers: () => (s) => s.subscribers,
})
export const {
    addViewer,
    removeViewer,
    addSubscriber,
    removeSubscriber,
    prepareToWatch,
    clearPreparingToWatch,
    subscribeToMap,
    unsubscribeToMap,
    publishState,
    setUpdatedViewers,
    receiveState,
    copyState,
    setWatching,
    pubsubTriggerNotification,
    disconnectSocket,
    clearPubsubState,
} = actions

export const {
    selectViewers,
    selectWatching,
    selectPreparingToWatch,
    selectMouseLocations,
    selectTriggerNotification,
    selectSubscribers,
} = selectors
export { r as reducer }
